import type { ShopLocale } from '../../i18n'

const hasControlCharacters = (value: string): boolean => {
  for (const char of value) {
    const code = char.charCodeAt(0)
    if (code < 32 || code === 127) return true
  }
  return false
}

export const isSafeNextPath = (value: string): boolean => {
  const candidate = value.trim()
  if (!candidate.startsWith('/')) return false
  if (candidate.startsWith('//')) return false
  if (candidate.includes('://')) return false
  if (hasControlCharacters(candidate)) return false
  return true
}

const isValidCheckoutSessionId = (value: string): boolean => /^[a-f0-9]{24}$/i.test(value.trim())

const normalizeCheckoutNextPath = (value: string): string => {
  const candidate = value.trim()
  if (!candidate) return candidate

  let parsed: URL
  try {
    parsed = new URL(candidate, 'https://emporion.test')
  } catch {
    return candidate
  }

  const segments = parsed.pathname
    .split('/')
    .map((chunk) => chunk.trim().toLowerCase())
    .filter(Boolean)
  const lastSegment = segments[segments.length - 1] ?? ''
  if (lastSegment !== 'checkout') return candidate

  const sessionId = parsed.searchParams.get('sessionId')?.trim() ?? ''
  if (!isValidCheckoutSessionId(sessionId)) {
    parsed.searchParams.delete('sessionId')
    parsed.searchParams.delete('session')
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  }

  parsed.searchParams.set('sessionId', sessionId)
  parsed.searchParams.delete('session')
  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

export const resolveNextPath = (defaultPath = '/dashboard', search?: string) => {
  const sourceSearch =
    typeof search === 'string'
      ? search
      : typeof window !== 'undefined'
        ? window.location.search
        : ''
  const candidate = new URLSearchParams(sourceSearch).get('next')?.trim() || defaultPath
  if (!isSafeNextPath(candidate)) return defaultPath
  return normalizeCheckoutNextPath(candidate)
}

export const buildAuthLink = (
  slug: string,
  nextPath: string,
  toLocalizedPath: (path: string) => string,
) => {
  const target = toLocalizedPath('/' + slug)
  const accountPath = toLocalizedPath('/dashboard')
  if (!nextPath || nextPath === '/dashboard' || nextPath === accountPath) {
    return target
  }
  return target + '?next=' + encodeURIComponent(nextPath)
}

export const formatDateTime = (
  value: string | null,
  locale: ShopLocale,
  emptyLabel: string,
) => {
  if (!value) return emptyLabel
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return emptyLabel
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed)
}

export const formatCurrency = (value: number, locale: ShopLocale, currency = 'EUR') =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value)

export const getInitials = (value: string, fallback = 'CL') =>
  value
    .split(/\s+/)
    .map((chunk) => chunk.trim()[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase() || fallback
